import { useMemo, useRef } from "react";

import type { ScoutConversationPrivateDocumentRow } from "./useScoutConversationPrivateDocuments";

import { SCOUT_LARGE_DOCUMENT_EXTRACTION_PROGRESS_STATUSES } from "../Chat/scoutLargeDocumentJobUi";
import { isTextExtractionDocument } from "./scoutPrivateDocumentQuery";

function pickWatchedDocument(
  rows: ScoutConversationPrivateDocumentRow[],
  privateDocumentId: string
): ScoutConversationPrivateDocumentRow | undefined {
  const document = rows.find((row) => row.id === privateDocumentId);
  if (
    !document ||
    !isTextExtractionDocument(document) ||
    !SCOUT_LARGE_DOCUMENT_EXTRACTION_PROGRESS_STATUSES.has(document.extractionStatus)
  ) {
    return undefined;
  }

  return document;
}

type UseScoutLargeDocumentExtractionProgressInput = {
  tenantId: string | undefined;
  conversationId: string | undefined;
  /** When set, show the in-thread extraction job UI for this document. */
  privateDocumentId?: string;
  documents: ScoutConversationPrivateDocumentRow[];
};

/**
 * Module-local cache of "when did we first observe a watched document for this
 * (conversation, document) pair". Lives outside React so the stamp survives
 * unmount/remount of the progress card (e.g. as the user scrolls the chat).
 * Read-only from React's perspective — no subscriptions, no notifications.
 */
const startedAtByWatchTarget = new Map<string, number>();

function getWatchTargetKey(
  conversationId: string | undefined,
  privateDocumentId: string | undefined
): string | undefined {
  if (!privateDocumentId) {
    return undefined;
  }

  return `${conversationId ?? "pending"}:${privateDocumentId}`;
}

/**
 * Tracks a single private document's extraction job for the in-thread progress card.
 *
 * Document rows are owned by the conversation-level SSE hook
 * (`useScoutPrivateDocumentStream` via `useScoutConversationPrivateDocuments`).
 * This hook is a passive view over those rows — it derives the watched document
 * and tracks the first-seen timestamp for the "started X ago" label.
 *
 * The "started at" timestamp is derived during render from a module-local
 * cache plus a ref, not from React state. Keeping it out of `useState` (and
 * out of effects that re-publish into the conversation store) is what avoids
 * the "Maximum update depth exceeded" loop we hit when SSE updates produced
 * fresh document references on every event.
 */
export function useScoutLargeDocumentExtractionProgress(
  input: UseScoutLargeDocumentExtractionProgressInput
) {
  const { conversationId, privateDocumentId, documents } = input;

  const document = useMemo(() => {
    if (!privateDocumentId) {
      return undefined;
    }

    return pickWatchedDocument(documents, privateDocumentId);
  }, [documents, privateDocumentId]);

  const watchTargetKey = getWatchTargetKey(conversationId, privateDocumentId);
  const hasDocument = document !== undefined;

  // Derive the start timestamp purely from inputs + the module-level cache so
  // we never observe a derived value and feed it back through state/effects.
  // The ref tracks which key this instance is currently watching so we can
  // forget the cached entry when the document goes away and start fresh on
  // re-appearance. Mutating the cache during render is safe because the writes
  // are idempotent (set once per key) and do not notify React subscribers.
  const lastSeenKeyRef = useRef<string | undefined>(undefined);

  let extractionStartedAtMs: number | undefined;
  if (hasDocument && watchTargetKey) {
    const cached = startedAtByWatchTarget.get(watchTargetKey);
    if (cached === undefined) {
      const next = Date.now();
      startedAtByWatchTarget.set(watchTargetKey, next);
      extractionStartedAtMs = next;
    } else {
      extractionStartedAtMs = cached;
    }
    lastSeenKeyRef.current = watchTargetKey;
  } else {
    extractionStartedAtMs = undefined;
    const previousKey = lastSeenKeyRef.current;
    if (previousKey !== undefined && previousKey !== watchTargetKey) {
      startedAtByWatchTarget.delete(previousKey);
      lastSeenKeyRef.current = undefined;
    }
  }

  return {
    document,
    extractionStartedAtMs,
    isVisible: hasDocument,
  };
}
